import { Link, Location } from '@reach/router';
import queryString from 'query-string';
import React, { SyntheticEvent, useState } from 'react';
import { Query } from 'react-apollo';
import ErrorMessages from '../components/ErrorMessages';
import { Switch } from '../components/forms';
import Input from '../components/forms/Input';
import { ErrorMessagesArray } from '../components/settings/tracker/types';
import { Button, ButtonFilled, ScrollView } from '../components/ui';
import logo from '../images/logo-blue.svg';
import { loginAndWelcome } from '../lib/auth';
import client from '../lib/client';
import { invitationQuery } from '../queries/invitations';
import { registerMutation } from '../queries/user';
import {
  RegisterMutation,
  RegisterMutationVariables
} from '../queries/__generated__/RegisterMutation';
import { ErrorScreen, Loading } from '../components/utils';

const Register: React.FC = () => {
  const [password, setPassword] = useState('');
  const [includeDemoNotebooks, setIncludeDemoNotebooks] = useState(true);
  const [errors, setErrors] = React.useState<ErrorMessagesArray>([]);

  async function registerUser(event: SyntheticEvent, inviteCode: string, email: string) {
    event.preventDefault();

    const res = await client.mutate<RegisterMutation, RegisterMutationVariables>({
      mutation: registerMutation,
      variables: {
        input: {
          password,
          includeDemoNotebooks,
          inviteCode
        }
      }
    });

    if (res.data!.register.errors!) {
      setErrors(res.data!.register.errors!);
      return;
    }

    loginAndWelcome({
      variables: {
        email,
        password: password
      }
    });
  }

  return (
    <Location>
      {({ location }) => {
        const query = queryString.parse(location.search);
        const inviteCode = query.inviteCode;

        return (
          <div className="fixed absolute--fill flex flex-column justify-between items-center pa4">
            <img className="w3" src={logo} alt="" />

            <Query query={invitationQuery} variables={{ inviteCode }} errorPolicy="all">
              {({ data, loading, error }) => {
                if (loading) return <Loading />;
                if (error) return <ErrorScreen error={error.message} />;

                if (!data || !data.findInvitation) {
                  return (
                    <div className="flex-auto flex flex-column items-center justify-center tc pa3">
                      <h1 className="display f2 mv0">Your invitation has expired.</h1>
                      <p className="serif mb4">
                        Not true? Get in touch and we&rsquo;ll get this ironed out for you.
                      </p>
                      <ButtonFilled as="a" href="https://www.quala.io/support/" target="blank">
                        Get Support
                      </ButtonFilled>
                    </div>
                  );
                }

                const {
                  findInvitation: { name, email }
                } = data;

                return (
                  <>
                    <div className="tc">
                      <h1 className="f3 f2-ns display tc mb0">Welcome {name}!</h1>
                      <div className="serif lh-copy measure center f6 mb4">
                        <p className="mb1">
                          We&rsquo;re thrilled to have you as one of the first users of Quala.
                        </p>
                        <p className="f6 mt0">
                          The email you&rsquo;ll use to log in is{' '}
                          <span className="fw5 i">{email}</span>. Create a secure password and
                          you&rsquo;re good to go.
                        </p>
                      </div>

                      <form onSubmit={event => registerUser(event, inviteCode as string, email)}>
                        {errors.length > 0 && (
                          <div className="mb3">
                            <ErrorMessages errors={errors} />
                          </div>
                        )}

                        <div className="mw5 center">
                          <Input
                            className="db mb3 w-100 tc"
                            label="Password"
                            id="password"
                            type="password"
                            name="password"
                            value={password}
                            onChange={event => setPassword(event.currentTarget.value)}
                            data-testid="password-input"
                          />

                          <div className="flex items-center justify-between mb4">
                            <span className="f6 navy">Include example spaces</span>
                            <Switch
                              active={includeDemoNotebooks}
                              setActive={setIncludeDemoNotebooks}
                            />
                          </div>
                        </div>

                        <ButtonFilled type="submit" data-testid="register-button">
                          Sign Up
                        </ButtonFilled>
                      </form>
                    </div>
                  </>
                );
              }}
            </Query>

            <div className="tc">
              <p className="f6 serif">Already have an account?</p>
              <Button as={Link} to="/login">
                Login
              </Button>
            </div>
          </div>
        );
      }}
    </Location>
  );
};

export default Register;
